import { CommonModule } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import {
    FieldErrorComponent, FormStepConfig, FormStepDirective, PublicFormMessage, PublicFormState, PublicFormSubmitResult,
    PublicStepFormComponent, SUBMIT,
} from '@shared/components/public-step-form/public-step-form.component';
import { SharedPrimeNgModule } from '@shared/modules/shared-prime-ng.module';
import {
    isValidDni, KEY_REQUEST_KEY_TYPE_OPTIONS, KEY_REQUEST_REASON_OPTIONS, KEY_REQUEST_RELATION_OPTIONS, normalizeDni,
} from '@pages/key-requests/key-requests.config';
import { KeyRequestsManagerService } from '@pages/key-requests/key-requests-manager.service';
import { KeyRequestFormData, KeyRequestFormMember, KeyRequestFormUnit, KeyRequestSubmit } from './key-request-form.config';
import { KeyRequestFormService } from './key-request-form.service';

/*
Formulario público de pedido de copia de llave (AF298). Se llega con el link que manda el agente (/pedido-llave?cod=...).
Paso "request": el pedido (unidad si tiene varias, para quién, datos que falten, tipo de llave y motivo).
Paso "emergency": contacto de emergencia, solo si la unidad no lo tiene.
Los pasos y sus transiciones están en `steps`; qué se muestra según lo elegido, en los getters.
Lo que se valida acá se vuelve a validar al guardar (KeyRequestsManagerService).
*/
@Component({
    selector: 'app-key-request-form',
    standalone: true,
    imports: [CommonModule, ReactiveFormsModule, SharedPrimeNgModule, PublicStepFormComponent, FormStepDirective, FieldErrorComponent],
    templateUrl: './key-request-form.component.html',
    styleUrl: './key-request-form.component.scss',
})
export class KeyRequestFormComponent implements OnInit {
    private route = inject(ActivatedRoute);
    private fb = inject(FormBuilder);
    private service = inject(KeyRequestFormService);
    private keyRequestsManager = inject(KeyRequestsManagerService);

    readonly relationOptions = KEY_REQUEST_RELATION_OPTIONS;
    readonly keyTypeOptions = KEY_REQUEST_KEY_TYPE_OPTIONS;
    readonly reasonOptions = KEY_REQUEST_REASON_OPTIONS;

    readonly messages: Partial<Record<PublicFormState, PublicFormMessage>> = {
        expired: {
            title: 'El enlace venció',
            text: 'El enlace dura 30 minutos. Pedí uno nuevo por WhatsApp y completá el formulario de nuevo.',
        },
        used: {
            title: 'Este enlace ya se usó',
            text: 'Ya registramos un pedido con este enlace. Si necesitás otra llave, pedí un enlace nuevo por WhatsApp.',
        },
        done: {
            title: '¡Pedido registrado!',
            text: 'La administración va a revisar tu pedido. Te vamos a avisar por WhatsApp cuando lo resuelva. Ya podés volver a WhatsApp.',
        },
    };

    // máquina de estados del formulario: cada paso se valida y va al primero de sus destinos que se cumpla
    readonly steps: FormStepConfig[] = [
        {
            id: 'request',
            validate: () => this.validateRequest(),
            transitions: [
                { to: 'emergency', when: () => this.needsEmergency },
                { to: SUBMIT },
            ],
        },
        {
            id: 'emergency',
            validate: () => this.validateEmergency(),
            transitions: [{ to: SUBMIT }],
        },
    ];

    state: PublicFormState = 'loading';
    cod = '';
    data?: KeyRequestFormData;

    form: FormGroup = this.fb.group({
        consorcistaId: [''],
        forWhom: [''],              // 'self' | 'other'
        otherType: [''],            // 'registered' | 'unregistered'
        registeredId: [''],
        recipientDni: [''],
        firstName: [''],
        lastName: [''],
        phoneArea: [''],
        phoneNumber: [''],
        relation: [''],
        relationOther: [''],
        keyType: [''],
        keyTypeOther: [''],
        reason: [''],
        lostKey: [false],
        pickupResponsibleDni: [''],
        emergency: this.fb.group({
            firstName: [''],
            lastName: [''],
            phoneArea: [''],
            phoneNumber: [''],
            relation: [''],
            accessKey: [false],
        }),
    });

    async ngOnInit() {
        /* ---------- GENERADOR DE LINK DE PRUEBA (solo test) ----------
           Para probar sin el agente: descomentar este bloque y el inject de KeyRequestsManagerService (arriba),
           poner un consorcista de prueba propio y abrir /pedido-llave sin cod. Crea un link nuevo (como la function
           createformlink) y lo muestra en la consola. Escribe en Firestore (keyRequestTokens). Después volver a comentar.
           Consorcistas de prueba (test, "Prueba Test 1", unidad 9-Z, números inventados): "[CLAUDE-TS] Solicitante"
           (dCU21Y8tihKbouxHFk1g) y "[CLAUDE-TS] Conviviente" (6pu3k3NrZCJZ5VfZciMc). Ya tienen DNI y el solicitante tiene
           contacto de emergencia: para volver a probar esos pasos, borrar dni / emergencyContact de esos documentos. */
        // const PRUEBA = { consorcioId: 'DJ6Scysd8L9sC5XOjzAm', consorcistaId: 'dCU21Y8tihKbouxHFk1g', preseleccionar: false };
        // if (!this.route.snapshot.queryParams['cod']) {
        //     const url = await this.keyRequestsManager.createTestToken(PRUEBA.consorcioId, PRUEBA.consorcistaId, PRUEBA.preseleccionar);
        //     console.info('[pedido-llave] link de prueba (vence en 30 minutos):', url);
        // }

        this.cod = this.route.snapshot.queryParams['cod'] ?? '';
        if (!this.cod) {
            this.state = 'invalid';
            return;
        }
        try {
            this.data = await this.service.load(this.cod);
        } catch (error) {
            console.error('[pedido-llave] error al cargar el formulario', error);
            this.state = 'error';
            return;
        }
        if (this.data.state !== 'ok' || !this.data.units?.length) {
            this.state = this.data.state === 'ok' ? 'invalid' : this.data.state;
            return;
        }

        const preselected = this.data.units.find(u => u.consorcistaId === this.data?.preselectedConsorcistaId);
        if (preselected || this.data.units.length === 1) {
            this.selectUnit((preselected ?? this.data.units[0]).consorcistaId);
        }
        this.form.get('reason')?.valueChanges.subscribe(reason => {
            if (reason !== 'replacement') {
                this.form.get('lostKey')?.setValue(false, { emitEvent: false });
            }
        });
        this.state = 'ok';
    }

    /* ---------- lo que se muestra según lo elegido ---------- */

    get units(): KeyRequestFormUnit[] {
        return this.data?.units ?? [];
    }

    // el selector de unidad solo aparece si tiene varias y el agente no mandó cuál
    get showUnitSelector(): boolean {
        return this.units.length > 1 && !this.data?.preselectedConsorcistaId;
    }

    get unit(): KeyRequestFormUnit | undefined {
        return this.units.find(u => u.consorcistaId === this.value('consorcistaId'));
    }

    get isRealEstate(): boolean {
        return this.unit?.occupancyRole === 'realEstate';
    }

    get members(): KeyRequestFormMember[] {
        return this.unit?.members ?? [];
    }

    get memberOptions(): { value: string, label: string }[] {
        return this.members.map(m => ({ value: m.consorcistaId, label: [m.firstName, m.lastName].filter(Boolean).join(' ') }));
    }

    get member(): KeyRequestFormMember | undefined {
        return this.members.find(m => m.consorcistaId === this.value('registeredId'));
    }

    get isOther(): boolean {
        return this.value('forWhom') === 'other';
    }

    get isRegistered(): boolean {
        return this.isOther && this.value('otherType') === 'registered';
    }

    get isUnregistered(): boolean {
        return this.isOther && this.value('otherType') === 'unregistered';
    }

    // DNI de quien recibe la llave: solo si no lo tenemos
    get needsRecipientDni(): boolean {
        if (this.value('forWhom') === 'self') {
            return !this.unit?.hasDni;
        }
        if (this.isRegistered) {
            return !!this.member && !this.member.hasDni;
        }
        return this.isUnregistered;
    }

    get needsEmergency(): boolean {
        return !!this.unit && !this.unit.hasEmergencyContact;
    }

    /* ---------- acciones ---------- */

    selectUnit(consorcistaId: string) {
        this.form.patchValue({ consorcistaId, registeredId: '' });
        const unit = this.unit;
        // una inmobiliaria no pide "para mí": la llave siempre es para otra persona
        if (unit?.occupancyRole === 'realEstate') {
            this.form.patchValue({ forWhom: 'other' });
        }
        // si no hay otras personas registradas, la única opción es "no está registrada"
        if (unit && unit.members.length === 0 && this.value('otherType') === 'registered') {
            this.form.patchValue({ otherType: 'unregistered' });
        }
    }

    setForWhom(forWhom: 'self' | 'other') {
        this.form.patchValue({ forWhom });
        if (forWhom === 'other' && !this.value('otherType') && this.members.length === 0) {
            this.form.patchValue({ otherType: 'unregistered' });
        }
    }

    // lo llama el contenedor al llegar a SUBMIT
    submit = async (): Promise<PublicFormSubmitResult> => {
        const result = await this.service.submit(this.cod, this.buildPayload());
        if (result.success) {
            return { state: 'done' };
        }
        switch (result.error) {
            case 'EXPIRED': return { state: 'expired' };
            case 'USED': return { state: 'used' };
            case 'INVALID': return { state: 'invalid' };
            case 'VALIDATION': return { error: (result.message || 'Hay datos que no son válidos') + '. Revisá los datos y volvé a enviar.' };
            default: return { error: 'No pudimos enviar el pedido. Probá de nuevo en unos minutos.' };
        }
    };

    /* ---------- validaciones de cada paso ---------- */

    private validateRequest(): Record<string, string> {
        const e: Record<string, string> = {};
        const required = 'Campo obligatorio';
        if (!this.unit) e['consorcistaId'] = 'Elegí la unidad';
        if (!this.value('forWhom')) e['forWhom'] = 'Elegí para quién es la llave';
        if (this.isOther) {
            if (!this.value('otherType')) e['otherType'] = 'Elegí una opción';
            if (this.isRegistered && !this.member) e['registeredId'] = 'Elegí a la persona';
            if (this.isUnregistered) {
                if (!this.text('firstName')) e['firstName'] = required;
                if (!this.text('lastName')) e['lastName'] = required;
                if (!this.text('phoneArea') || !this.text('phoneNumber')) e['phone'] = 'Completá el código de área y el número';
            }
            if (!this.value('relation')) e['relation'] = 'Elegí el vínculo';
            if (this.value('relation') === 'other' && !this.text('relationOther')) e['relationOther'] = 'Contanos cuál es el vínculo';
        }
        if (this.needsRecipientDni && !isValidDni(this.value('recipientDni'))) e['recipientDni'] = 'Ingresá un DNI válido (solo números)';
        if (this.isRealEstate && !isValidDni(this.value('pickupResponsibleDni'))) e['pickupResponsibleDni'] = 'Ingresá un DNI válido (solo números)';
        if (!this.value('keyType')) e['keyType'] = 'Elegí el tipo de llave';
        if (this.value('keyType') === 'other' && !this.text('keyTypeOther')) e['keyTypeOther'] = 'Contanos qué tipo de llave es';
        if (!this.value('reason')) e['reason'] = 'Elegí el motivo';
        return e;
    }

    private validateEmergency(): Record<string, string> {
        const e: Record<string, string> = {};
        const em = this.form.get('emergency')?.value ?? {};
        if (!String(em.firstName ?? '').trim()) e['em.firstName'] = 'Campo obligatorio';
        if (!String(em.lastName ?? '').trim()) e['em.lastName'] = 'Campo obligatorio';
        if (!String(em.phoneArea ?? '').trim() || !String(em.phoneNumber ?? '').trim()) e['em.phone'] = 'Completá el código de área y el número';
        if (!String(em.relation ?? '').trim()) e['em.relation'] = 'Contanos qué relación tiene con vos';
        return e;
    }

    private buildPayload(): KeyRequestSubmit {
        const forWhom = this.value('forWhom');
        const type = forWhom === 'self' ? 'self' : (this.isRegistered ? 'registered' : 'unregistered');
        const payload: KeyRequestSubmit = {
            consorcistaId: this.value('consorcistaId'),
            recipient: { type },
            keyType: this.value('keyType'),
            reason: this.value('reason'),
            lostKey: this.value('reason') === 'replacement' && !!this.value('lostKey'),
        };
        if (type === 'registered') {
            payload.recipient.consorcistaId = this.value('registeredId');
        }
        if (type === 'unregistered') {
            payload.recipient.firstName = this.text('firstName');
            payload.recipient.lastName = this.text('lastName');
            payload.recipient.phone = { countryCode: '54', phoneArea: this.text('phoneArea'), phoneNumber: this.text('phoneNumber') };
        }
        if (this.needsRecipientDni) {
            payload.recipient.dni = normalizeDni(this.value('recipientDni'));
        }
        if (type !== 'self') {
            payload.recipient.relation = this.value('relation');
            if (this.value('relation') === 'other') {
                payload.recipient.relationOther = this.text('relationOther');
            }
        }
        if (this.isRealEstate) {
            payload.pickupResponsibleDni = normalizeDni(this.value('pickupResponsibleDni'));
        }
        if (this.value('keyType') === 'other') {
            payload.keyTypeOther = this.text('keyTypeOther');
        }
        if (this.needsEmergency) {
            const em = this.form.get('emergency')?.value;
            payload.emergencyContact = {
                firstName: String(em.firstName).trim(),
                lastName: String(em.lastName).trim(),
                countryCode: '54',
                phoneArea: String(em.phoneArea).trim(),
                phoneNumber: String(em.phoneNumber).trim(),
                relation: String(em.relation).trim(),
                accessKey: !!em.accessKey,
            };
        }
        return payload;
    }

    private value(name: string): any {
        return this.form.get(name)?.value;
    }

    private text(name: string): string {
        return String(this.value(name) ?? '').trim();
    }
}
